import { EventBus } from '../core/Events';
import type { GameSave, WorldMeta } from './SaveManager';

/**
 * Bulut kayıt senkronizasyonu.
 *
 * Tasarım ilkeleri:
 *  1. YEREL KAYIT ASLA DEVRE DIŞI KALMAZ. Bulut yalnızca üstüne biner.
 *     İnternet yoksa oyun hiç etkilenmez, kayıt kuyruğa alınır.
 *  2. KOTA DOSTU. Otomatik kayıt 30 sn'de bir yerel yazar ama buluta
 *     en fazla PUSH_INTERVAL aralığında gönderir (+ çıkışta bir kez).
 *  3. ÇAKIŞMA SESSİZCE VERİ SİLMEZ. Sunucu 409 dönerse o dünya "çakışmalı"
 *     işaretlenir, senkron durur ve kullanıcı menüden seçim yapar.
 */

const TOKEN_KEY = 'kg.cloud.token';
const REV_KEY = 'kg.cloud.revs';
const QUEUE_KEY = 'kg.cloud.queue';
const ENABLED_KEY = 'kg.cloud.enabled';

/** Aynı dünya için iki bulut yazımı arasındaki en kısa süre (saniye). */
const PUSH_INTERVAL = 180;

export type SyncStatus = 'disabled' | 'offline' | 'connecting' | 'ready' | 'syncing' | 'conflict' | 'quota' | 'error';

export interface CloudWorld {
  id: string;
  name: string;
  mode: 'story' | 'normal';
  difficulty: string;
  seed: number;
  day: number;
  playtime: number;
  rev: number;
  sizeBytes: number;
  createdAt: number;
  updatedAt: number;
}

export interface CloudProfile {
  id: string;
  name: string;
  avatar: string | null;
  createdAt: number;
  stats: Record<string, number>;
  achievements: string[];
}

type SyncEvents = {
  'status:changed': { status: SyncStatus; message?: string };
  'conflict': { worldId: string; serverSave: GameSave | null; serverPlaytime?: number };
  'pushed': { worldId: string; rev: number; sizeBytes: number };
  'ready': { profile: CloudProfile };
};

export class CloudSync {
  readonly events = new EventBus<SyncEvents>();
  status: SyncStatus = 'disabled';
  statusMessage = '';
  profile: CloudProfile | null = null;

  private token: string | null = null;
  private revs: Record<string, number> = {};
  private lastPush: Record<string, number> = {};
  private conflicts = new Set<string>();
  private inFlight = false;
  private retryTimer: number | null = null;

  constructor(private baseUrl = '/api') {
    this.token = this.read(TOKEN_KEY);
    this.revs = this.readJson<Record<string, number>>(REV_KEY) ?? {};
    // İnternet geri geldiğinde kuyruğu boşalt
    window.addEventListener('online', () => void this.flushQueue());
  }

  get enabled(): boolean {
    return this.read(ENABLED_KEY) !== '0';
  }

  setEnabled(on: boolean): void {
    this.write(ENABLED_KEY, on ? '1' : '0');
    if (!on) {
      this.setStatus('disabled');
    } else {
      void this.init();
    }
  }

  get signedIn(): boolean {
    return !!this.token && !!this.profile;
  }

  hasConflict(worldId: string): boolean {
    return this.conflicts.has(worldId);
  }

  clearConflict(worldId: string): void {
    this.conflicts.delete(worldId);
  }

  /** Oturumu kurar: token yoksa anonim hesap açar, varsa doğrular. */
  async init(): Promise<boolean> {
    if (!this.enabled) {
      this.setStatus('disabled');
      return false;
    }
    // Tek dosya / file:// sürümünde sunucu yoktur: hiç denemeden çevrimdışı çalış.
    if (typeof location !== 'undefined' && location.protocol === 'file:') {
      this.setStatus('disabled', 'Tek dosya sürümü: yalnızca cihazda kayıt.');
      return false;
    }
    this.setStatus('connecting');
    try {
      const res = await this.request('POST', '/auth', this.token ? undefined : {});
      // Sunucu tarafı hiç kurulmamışsa (saf statik barındırma) ısrar etmeyiz.
      if (res.status === 404 || res.status === 405) {
        this.setStatus('disabled', 'Bu sunucuda bulut kaydı yok: yalnızca cihazda kayıt.');
        return false;
      }
      if (!res.ok) {
        // Token geçersizse (ör. sunucu sıfırlandı) yeni hesap dene
        if (res.status === 401 && this.token) {
          this.token = null;
          try { localStorage.removeItem(TOKEN_KEY); } catch { /* yoksay */ }
          return this.init();
        }
        this.setStatus('error', `Sunucu yanıtı: ${res.status}`);
        return false;
      }
      const body = (await res.json()) as { token?: string; player: CloudProfile };
      if (body.token) {
        this.token = body.token;
        this.write(TOKEN_KEY, body.token);
      }
      this.profile = body.player;
      this.setStatus('ready');
      this.events.emit('ready', { profile: body.player });
      void this.flushQueue();
      return true;
    } catch (err) {
      // Ağ yok: sessizce çevrimdışı moda geç, oyun etkilenmez
      this.setStatus('offline', String(err).slice(0, 120));
      return false;
    }
  }

  /** Kaydı buluta gönder. throttle=false ise aralık beklenmez (çıkışta kullanılır). */
  async push(save: GameSave, opts: { throttle?: boolean; force?: boolean } = {}): Promise<boolean> {
    // Oturum hazırlanırken oluşturulan dünya kaybolmasın: önce yerele kuyruğa
    // yazılır, init() token alınca flushQueue bunu gönderir.
    if (!this.enabled) return false;
    this.enqueue(save);
    if (!this.token) return false;
    const id = save.meta.id;
    if (this.conflicts.has(id) && !opts.force) return false;

    const now = Date.now() / 1000;
    if (opts.throttle !== false && (this.lastPush[id] ?? 0) + PUSH_INTERVAL > now) return false;

    // Her durumda kuyruğa yaz: gönderim başarısız olursa kaybolmaz
    this.enqueue(save);

    if (this.inFlight) return false;
    this.inFlight = true;
    this.setStatus('syncing');
    try {
      const url = `/worlds/${encodeURIComponent(id)}${opts.force ? '?force=1' : ''}`;
      const res = await this.request('PUT', url, {
        rev: opts.force ? undefined : (this.revs[id] ?? 0),
        meta: {
          name: save.meta.name,
          mode: save.meta.mode,
          difficulty: save.meta.difficulty,
          seed: save.meta.seed,
          day: save.meta.day,
          playtime: save.meta.playtime,
        },
        save,
      });

      if (res.status === 409) {
        const body = (await res.json()) as { code: string; rev: number; server?: GameSave; serverPlaytime?: number };
        this.conflicts.add(id);
        this.revs[id] = body.rev;
        this.persistRevs();
        this.setStatus('conflict', 'Bulutta daha yeni kayıt var.');
        this.events.emit('conflict', { worldId: id, serverSave: body.server ?? null, serverPlaytime: body.serverPlaytime });
        return false;
      }
      if (res.status === 429) {
        this.setStatus('quota', 'Günlük bulut kotası doldu, yerel kayıt sürüyor.');
        return false;
      }
      if (!res.ok) {
        this.setStatus('error', `Gönderim başarısız (${res.status})`);
        return false;
      }

      const body = (await res.json()) as { rev: number; sizeBytes: number };
      this.revs[id] = body.rev;
      this.persistRevs();
      this.lastPush[id] = now;
      this.dequeue(id);
      this.setStatus('ready');
      this.events.emit('pushed', { worldId: id, rev: body.rev, sizeBytes: body.sizeBytes });
      return true;
    } catch (err) {
      this.setStatus('offline', String(err).slice(0, 120));
      this.scheduleRetry();
      return false;
    } finally {
      this.inFlight = false;
    }
  }

  /**
   * Sayfa kapanırken bekleyen kaydı gönderir.
   * sendBeacon yerine keepalive'lı fetch kullanılır: sendBeacon Authorization
   * başlığı taşıyamadığı için token'ı URL'ye koymak gerekirdi ve URL'ler
   * sunucu loglarına düşer. keepalive başlık taşır ve sekme kapanırken de sürer
   * (gövde sınırı 64 KB; bizim kayıtlar birkaç KB).
   */
  flushOnExit(save: GameSave | null): void {
    if (!this.enabled || !this.token || !save) return;
    const id = save.meta.id;
    if (this.conflicts.has(id)) return;
    this.enqueue(save);
    const payload = JSON.stringify({
      rev: this.revs[id] ?? 0,
      meta: {
        name: save.meta.name,
        mode: save.meta.mode,
        difficulty: save.meta.difficulty,
        seed: save.meta.seed,
        day: save.meta.day,
        playtime: save.meta.playtime,
      },
      save,
    });
    try {
      void fetch(`${this.baseUrl}/worlds/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${this.token}`, 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {
        /* kapanış anında hata yutulur; kayıt kuyrukta kalır */
      });
    } catch {
      /* yoksay: yerel kayıt zaten yazıldı */
    }
  }

  async list(): Promise<CloudWorld[]> {
    if (!this.enabled || !this.token) return [];
    try {
      const res = await this.request('GET', '/worlds');
      if (!res.ok) return [];
      const body = (await res.json()) as { worlds: CloudWorld[] };
      return body.worlds ?? [];
    } catch {
      this.setStatus('offline');
      return [];
    }
  }

  async pull(worldId: string): Promise<GameSave | null> {
    if (!this.enabled || !this.token) return null;
    try {
      const res = await this.request('GET', `/worlds/${encodeURIComponent(worldId)}`);
      if (!res.ok) return null;
      const body = (await res.json()) as { rev: number; save: GameSave };
      this.revs[worldId] = body.rev;
      this.persistRevs();
      this.conflicts.delete(worldId);
      return body.save;
    } catch {
      this.setStatus('offline');
      return null;
    }
  }

  async remove(worldId: string): Promise<boolean> {
    if (!this.enabled || !this.token) return false;
    try {
      const res = await this.request('DELETE', `/worlds/${encodeURIComponent(worldId)}`);
      if (res.ok) {
        delete this.revs[worldId];
        this.persistRevs();
        this.dequeue(worldId);
      }
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Hesap durumu: anonim mi, adlı mı. */
  async accountStatus(): Promise<{ anonymous: boolean; name: string | null } | null> {
    if (!this.enabled || !this.token) return null;
    try {
      const res = await this.request('GET', '/account');
      if (!res.ok) return null;
      return (await res.json()) as { anonymous: boolean; name: string | null };
    } catch {
      return null;
    }
  }

  /** Anonim hesabı adlı hesaba çevirir (veri korunur). */
  async register(name: string, password: string): Promise<{ ok: boolean; message: string }> {
    return this.accountAction({ action: 'register', name, password });
  }

  /** Başka cihazdan giriş: yeni token alır. */
  async login(name: string, password: string): Promise<{ ok: boolean; message: string }> {
    const res = await this.accountAction({ action: 'login', name, password });
    if (res.ok) {
      // Giriş yeni bir hesaba geçtiği için yerel sürüm bilgisi geçersiz
      this.revs = {};
      this.persistRevs();
      this.conflicts.clear();
      await this.init();
    }
    return res;
  }

  /** Tek kullanımlık kurtarma kodu üretir. */
  async recoveryCode(): Promise<{ ok: boolean; code?: string; message: string }> {
    if (!this.enabled || !this.token) return { ok: false, message: 'Bulut kapalı.' };
    try {
      const res = await this.request('POST', '/account', { action: 'recovery' });
      const body = (await res.json()) as { ok?: boolean; code?: string; message?: string };
      return { ok: res.ok, code: body.code, message: body.message ?? (res.ok ? 'Kod üretildi.' : 'Kod üretilemedi.') };
    } catch {
      return { ok: false, message: 'Bağlantı yok.' };
    }
  }

  /** Kurtarma kodu ile hesabı bu cihaza bağlar. */
  async claim(code: string): Promise<{ ok: boolean; message: string }> {
    const res = await this.accountAction({ action: 'claim', code });
    if (res.ok) {
      this.revs = {};
      this.persistRevs();
      this.conflicts.clear();
      await this.init();
    }
    return res;
  }

  private async accountAction(payload: Record<string, unknown>): Promise<{ ok: boolean; message: string }> {
    if (!this.enabled) return { ok: false, message: 'Bulut kaydı kapalı.' };
    try {
      const res = await this.request('POST', '/account', payload);
      const raw = await res.text();
      const contentType = res.headers.get('content-type') ?? '';
      if (!contentType.includes('application/json')) {
        // HTML yanıtı yalnızca Pages yönlendirmesi, edge hata sayfası veya proxy
        // katmanından gelebilir; Function API bütün yollarında JSON döndürür.
        // HTTP durumunu göstermek, kullanıcı verisi açığa çıkarmadan canlı teşhisi
        // mümkün kılar.
        return {
          ok: false,
          message: `Hesap API’si JSON yerine HTML yanıtı döndürdü (HTTP ${res.status}). Bu bir sunucu yönlendirme hatasıdır.`,
        };
      }

      let body: { ok?: boolean; token?: string; message?: string };
      try {
        body = JSON.parse(raw) as { ok?: boolean; token?: string; message?: string };
      } catch {
        return { ok: false, message: 'Hesap API’si geçersiz bir yanıt döndürdü. Lütfen tekrar dene.' };
      }
      if (res.ok && body.token) {
        this.token = body.token;
        this.write(TOKEN_KEY, body.token);
      }
      return { ok: res.ok, message: body.message ?? (res.ok ? 'Tamam.' : 'İşlem başarısız.') };
    } catch (err) {
      return { ok: false, message: `Bağlantı hatası: ${String(err).slice(0, 80)}` };
    }
  }

  async updateProfile(patch: { name?: string; avatar?: string | null; stats?: Record<string, number>; achievements?: string[] }): Promise<boolean> {
    if (!this.enabled || !this.token) return false;
    try {
      const res = await this.request('PUT', '/worlds', patch);
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Çakışmayı çöz: bulutu al ya da yereli zorla gönder. */
  async resolveConflict(worldId: string, choice: 'cloud' | 'local', localSave?: GameSave): Promise<GameSave | null> {
    if (choice === 'cloud') {
      const save = await this.pull(worldId);
      if (save) this.conflicts.delete(worldId);
      return save;
    }
    if (localSave) {
      this.conflicts.delete(worldId);
      const ok = await this.push(localSave, { throttle: false, force: true });
      if (!ok) this.conflicts.add(worldId);
    }
    return null;
  }

  // ---------------------------------------------------------------- kuyruk

  private enqueue(save: GameSave): void {
    const q = this.readJson<Record<string, GameSave>>(QUEUE_KEY) ?? {};
    q[save.meta.id] = save;
    // Kuyruk sınırsız büyümesin
    const keys = Object.keys(q);
    if (keys.length > 5) delete q[keys[0]!];
    this.writeJson(QUEUE_KEY, q);
  }

  private dequeue(worldId: string): void {
    const q = this.readJson<Record<string, GameSave>>(QUEUE_KEY) ?? {};
    if (q[worldId]) {
      delete q[worldId];
      this.writeJson(QUEUE_KEY, q);
    }
  }

  /** Bekleyen kayıtları gönder (bağlantı geri geldiğinde). */
  async flushQueue(): Promise<void> {
    if (!this.enabled || !this.token) return;
    const q = this.readJson<Record<string, GameSave>>(QUEUE_KEY) ?? {};
    for (const [id, save] of Object.entries(q)) {
      if (this.conflicts.has(id)) continue;
      await this.push(save, { throttle: false });
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer !== null) return;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      void this.flushQueue();
    }, 30_000);
  }

  // ---------------------------------------------------------------- altyapı

  private async request(method: string, path: string, body?: unknown): Promise<Response> {
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    // Uzun sürerse oyunu bekletmemek için zaman aşımı
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 12_000);
    try {
      return await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: ctrl.signal,
        credentials: 'omit',
      });
    } finally {
      window.clearTimeout(timer);
    }
  }

  private setStatus(status: SyncStatus, message = ''): void {
    if (this.status === status && this.statusMessage === message) return;
    this.status = status;
    this.statusMessage = message;
    this.events.emit('status:changed', { status, message });
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* depolama kapalıysa sessizce geç */
    }
  }
  private readJson<T>(key: string): T | null {
    const raw = this.read(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
  private writeJson(key: string, value: unknown): void {
    try {
      this.write(key, JSON.stringify(value));
    } catch {
      /* yoksay */
    }
  }
  private persistRevs(): void {
    this.writeJson(REV_KEY, this.revs);
  }

  /** Yerel dünya listesiyle bulut listesini birleştirir (UI için). */
  mergeWorldLists(local: WorldMeta[], cloud: CloudWorld[]): Array<WorldMeta & { cloud?: CloudWorld; onlyCloud?: boolean; conflict?: boolean }> {
    const out: Array<WorldMeta & { cloud?: CloudWorld; onlyCloud?: boolean; conflict?: boolean }> = [];
    const seen = new Set<string>();
    for (const w of local) {
      const c = cloud.find((x) => x.id === w.id);
      out.push({ ...w, cloud: c, conflict: this.conflicts.has(w.id) });
      seen.add(w.id);
    }
    for (const c of cloud) {
      if (seen.has(c.id)) continue;
      out.push({
        id: c.id,
        name: c.name,
        mode: c.mode,
        difficulty: c.difficulty,
        seed: c.seed,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        playtime: c.playtime,
        day: c.day,
        cloud: c,
        onlyCloud: true,
      });
    }
    out.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
    return out;
  }
}
